import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FaColumns,
  FaAngleDown,
  FaChevronLeft,
  FaChevronRight,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { MdOutlineFileDownload } from "react-icons/md";
import { format } from "date-fns";

import ModifyColumns from "./../Tools/ModifyColumns";
import "./NotoSans-Regular-normal";
import { apiRequestBodyBuilder } from "../config/tableHelper";
import { generateCSV, generatePDF } from "../Components/DownloadFuncs";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full bg-white p-5">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PAGE_SIZES = [5, 10, 15, 25];

const getColumnKey = (column) =>
  column?.accessor ??
  column?.accessorKey ??
  column?.key ??
  column?.field ??
  column?.id ??
  column?.name;

const getColumnLabel = (column) =>
  column?.header ??
  column?.label ??
  column?.title ??
  column?.name ??
  getColumnKey(column) ??
  "";

const isNumeric = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  !Number.isNaN(Number(String(value).replace(/[$,%\s,]/g, "")));

const toNumber = (value) => {
  if (!isNumeric(value)) return 0;

  return Number(String(value).replace(/[$,%\s,]/g, ""));
};

const cleanLabel = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const isRateColumn = (key, label) => {
  const text = `${cleanLabel(key)} ${cleanLabel(label)}`;

  return (
    text.includes("ctr") ||
    text.includes("cvr") ||
    text.includes("conversion rate") ||
    text.includes("conv rate") ||
    text.includes("rate")
  );
};

const isCurrencyColumn = (key, label) => {
  const text = `${cleanLabel(key)} ${cleanLabel(label)}`;

  return (
    text.includes("cost") ||
    text.includes("spend") ||
    text.includes("cpc") ||
    text.includes("cpm") ||
    text.includes("roas") ||
    text.includes("revenue")
  );
};

const isAverageMetric = (key, label) => {
  const text = `${cleanLabel(key)} ${cleanLabel(label)}`;

  return (
    text.includes("cpc") ||
    text.includes("cpm") ||
    text.includes("cost per") ||
    text.includes("avg") ||
    text.includes("average")
  );
};

const formatCellValue = (value, column) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const key = getColumnKey(column);
  const label = getColumnLabel(column);

  if (!isNumeric(value)) {
    return String(value);
  }

  const number = toNumber(value);

  if (isRateColumn(key, label)) {
    return `${number.toFixed(2)}%`;
  }

  if (isCurrencyColumn(key, label)) {
    return `$${number.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  return number.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
};

const getTotalValue = (rows, column) => {
  const key = getColumnKey(column);
  const label = getColumnLabel(column);

  if (!key || !rows.length) {
    return "";
  }

  const normalized = `${cleanLabel(key)} ${cleanLabel(label)}`;

  if (
    normalized.includes("month") ||
    normalized.includes("date") ||
    normalized === "period"
  ) {
    return "Total";
  }

  const numericRows = rows.filter((row) => isNumeric(row?.[key]));

  if (!numericRows.length) {
    return "—";
  }

  const totalCost = rows.reduce((sum, row) => {
    const rowKey = Object.keys(row || {}).find((k) =>
      cleanLabel(k).includes("cost"),
    );

    return sum + (rowKey ? toNumber(row[rowKey]) : 0);
  }, 0);

  const totalClicks = rows.reduce((sum, row) => {
    const rowKey = Object.keys(row || {}).find((k) =>
      cleanLabel(k).includes("click"),
    );

    return sum + (rowKey ? toNumber(row[rowKey]) : 0);
  }, 0);

  const totalImpressions = rows.reduce((sum, row) => {
    const rowKey = Object.keys(row || {}).find(
      (k) => cleanLabel(k).includes("impression") || cleanLabel(k) === "impr",
    );

    return sum + (rowKey ? toNumber(row[rowKey]) : 0);
  }, 0);

  const totalConversions = rows.reduce((sum, row) => {
    const rowKey = Object.keys(row || {}).find((k) =>
      cleanLabel(k).includes("conversion"),
    );

    return sum + (rowKey ? toNumber(row[rowKey]) : 0);
  }, 0);

  if (isRateColumn(key, label)) {
    if (normalized.includes("ctr") || normalized.includes("click through")) {
      return totalImpressions ? (totalClicks / totalImpressions) * 100 : 0;
    }

    return totalClicks ? (totalConversions / totalClicks) * 100 : 0;
  }

  if (isAverageMetric(key, label)) {
    if (normalized.includes("cpc")) {
      return totalClicks ? totalCost / totalClicks : 0;
    }

    if (normalized.includes("cost per") || normalized.includes("conversion")) {
      return totalConversions ? totalCost / totalConversions : 0;
    }

    return (
      numericRows.reduce((sum, row) => sum + toNumber(row[key]), 0) /
      numericRows.length
    );
  }

  return numericRows.reduce((sum, row) => sum + toNumber(row[key]), 0);
};

const getDescription = (title) => {
  const descriptions = {
    "Best Months":
      "Performance overview of the top-performing months based on onboarding audit metrics.",
  };

  return (
    descriptions[title] ||
    `Performance overview of ${String(title || "this report").toLowerCase()}.`
  );
};

const ReusableTable = ({
  title,
  apiEndpoint,
  columnsConfig,
  dateRange,
  apiRequestBodyExtras = {},
  projectId,
  customerId,
  selectedProject,
}) => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState(columnsConfig || []);
  const [loading, setLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(true);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const downloadRef = useRef(null);
  const columnRef = useRef(null);

  useEffect(() => {
    setColumns(columnsConfig || []);
  }, [columnsConfig]);

  useEffect(() => {
    if (!projectId || !customerId || !selectedProject) {
      setData([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      try {
        const reqBody = apiRequestBodyBuilder(dateRange);

        const rewriteFields = {
          ad_account_id: selectedProject?.ad_accounts?.[0]?.id,
          customer_id: selectedProject?.customer_id ?? customerId,
          platform: selectedProject?.ad_accounts?.[0]?.platform,
          project_id: selectedProject?.project_id ?? projectId,
        };

        const requestBody = {
          ...reqBody,
          ...apiRequestBodyExtras,
          ...rewriteFields,
        };

        const response = await fetch(
          `https://api.confidanto.com/${apiEndpoint}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          },
        );

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const responseData = await response.json();

        setData(Array.isArray(responseData) ? responseData : []);
      } catch (error) {
        console.error(`Error fetching data for ${title}:`, error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    dateRange,
    apiEndpoint,
    title,
    projectId,
    customerId,
    selectedProject,
    apiRequestBodyExtras,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }

      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setShowColumnsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, data, dateRange]);

  const handleSort = (key) => {
    if (!key || !data.length) {
      return;
    }

    setSortConfig((previous) => ({
      key,
      direction:
        previous.key === key && previous.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || !sortConfig.key) {
      return data;
    }

    return [...data].sort((a, b) => {
      const aValue = a?.[sortConfig.key];
      const bValue = b?.[sortConfig.key];

      if (isNumeric(aValue) && isNumeric(bValue)) {
        return sortConfig.direction === "asc"
          ? toNumber(aValue) - toNumber(bValue)
          : toNumber(bValue) - toNumber(aValue);
      }

      return (
        String(aValue ?? "").localeCompare(String(bValue ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * (sortConfig.direction === "asc" ? 1 : -1)
      );
    });
  }, [data, sortConfig]);

  const safeData = Array.isArray(sortedData) ? sortedData : [];

  const totalPages = Math.max(1, Math.ceil(safeData.length / rowsPerPage));

  const currentData = safeData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const totalRow = useMemo(
    () =>
      columns.reduce((row, column) => {
        const key = getColumnKey(column);

        row[key] = getTotalValue(data, column);

        return row;
      }, {}),
    [columns, data],
  );

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort className="text-[9px] opacity-50" />;
    }

    return sortConfig.direction === "asc" ? (
      <FaSortUp className="text-[9px]" />
    ) : (
      <FaSortDown className="text-[9px]" />
    );
  };

  const handleDownload = async (type) => {
    setShowDownloadMenu(false);
    setIsDownloading(true);

    try {
      const filename = `${String(title || "Report")
        .replace(/ /g, "_")
        .replace(/[^a-zA-Z0-9_-]/g, "")}_Report_${format(
        new Date(),
        "yyyy-MM-dd",
      )}.${type}`;

      let dateRangeStr = "All time";

      if (dateRange?.length > 0) {
        const start = dateRange[0]?.startDate;
        const end = dateRange[0]?.endDate;

        if (start && end) {
          dateRangeStr =
            format(start, "MMM dd, yyyy") === format(end, "MMM dd, yyyy")
              ? format(start, "MMM dd, yyyy")
              : `${format(start, "MMM dd, yyyy")} - ${format(
                  end,
                  "MMM dd, yyyy",
                )}`;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      if (type === "pdf") {
        await generatePDF(safeData, columns, dateRangeStr, filename, title);
      }

      if (type === "csv") {
        await generateCSV(safeData, columns, dateRangeStr, filename, title);
      }
    } catch (error) {
      console.error(`Failed to generate ${type.toUpperCase()}:`, error);
    } finally {
      setIsDownloading(false);
    }
  };

  const showEmptyState = !projectId || !customerId || !selectedProject;

  return (
    <section className="mx-10 mb-6 overflow-visible rounded-xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
      {/* Section Header */}
      <div
        className="flex cursor-pointer items-start justify-between gap-4 px-5 py-4"
        onClick={() => setIsOpen((value) => !value)}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FaAngleDown
              className={`shrink-0 text-lg text-slate-700 transition-transform duration-200 ${
                isOpen ? "rotate-0" : "-rotate-90"
              }`}
            />

            <h2 className="text-lg font-semibold tracking-[-0.01em] text-slate-900">
              {title}
            </h2>
          </div>

          <p className="ml-[21px] mt-1 text-sm leading-4 text-slate-500">
            {getDescription(title)}
          </p>
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-slate-100 px-5 pb-4 pt-3">
          {showEmptyState ? (
            <div className="flex min-h-[180px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50">
              <p className="text-sm text-slate-500">
                Please select a project with a valid Customer ID to view data.
              </p>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="mb-2 flex items-center justify-end gap-1">
                {/* Download */}
                <div className="relative" ref={downloadRef}>
                  <button
                    type="button"
                    disabled={loading || isDownloading}
                    onClick={() => setShowDownloadMenu((value) => !value)}
                    className={`px-4 py-2 rounded flex flex-col justify-center items-center transition-all ${
                      loading || isDownloading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-slate-100"
                    } ${showDownloadMenu ? "bg-blue-100 text-blue-600" : "bg-transparent text-gray-600"}`}
                  >
                    {isDownloading ? (
                      <svg
                        className="animate-spin h-5 w-5 mb-1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <MdOutlineFileDownload className="text-xl mb-1" />
                    )}
                    <h2 className="text-sm">
                      {isDownloading ? "Downloading" : "Download"}
                    </h2>
                  </button>

                  {showDownloadMenu && (
                    <div className="absolute right-0 z-40 w-36 bg-white shadow-lg rounded-lg mt-2 p-2 border">
                      <div
                        className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                        onClick={() => handleDownload("pdf")}
                      >
                        .PDF
                      </div>
                      <div
                        className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                        onClick={() => handleDownload("csv")}
                      >
                        .CSV
                      </div>
                    </div>
                  )}
                </div>

                {/* Columns */}
                <div className="relative" ref={columnRef}>
                  <button
                    type="button"
                    onClick={() => setShowColumnsMenu((value) => !value)}
                    className="bg-transparent text-gray-600 px-4 py-2 rounded hover:bg-slate-100 h-full flex flex-col justify-center items-center"
                  >
                    <FaColumns className="text-sm" />
                    <h4 className="text-sm mt-1.5">Columns</h4>
                  </button>

                  {showColumnsMenu && (
                    <div className="absolute right-0 top-9 z-50">
                      <ModifyColumns
                        columns={columns}
                        setColumns={setColumns}
                        setShowColumnsMenu={setShowColumnsMenu}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-auto rounded-lg shadow-md border border-gray-300 ml-9">
                {loading ? (
                  <TableSkeleton />
                ) : currentData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] border-collapse text-left">
                      {/* Table Header */}
                      <thead>
                        <tr className="bg-[#283493] text-white">
                          {columns.map((column, index) => {
                            const key = getColumnKey(column);
                            const label = getColumnLabel(column);

                            return (
                              <th
                                key={key || index}
                                scope="col"
                                onClick={() => handleSort(key)}
                                className="cursor-pointer select-none whitespace-nowrap border-r border-white/10 px-3 py-2.5 text-sm font-semibold tracking-[0.02em] last:border-r-0 hover:bg-[#222d82]"
                              >
                                <div className="flex items-center gap-1">
                                  <span>{label}</span>
                                  {key && renderSortIcon(key)}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>

                      {/* Table Body */}
                      <tbody>
                        {currentData.map((row, rowIndex) => (
                          <tr
                            key={row?.id ?? row?._id ?? rowIndex}
                            className="border-b border-slate-100 bg-white transition hover:bg-slate-50/80"
                          >
                            {columns.map((column, columnIndex) => {
                              const key = getColumnKey(column);

                              return (
                                <td
                                  key={key || columnIndex}
                                  className={`whitespace-nowrap px-3 py-2.5 text-sm ${
                                    columnIndex === 0
                                      ? "font-medium text-slate-800"
                                      : "text-slate-600"
                                  }`}
                                >
                                  {formatCellValue(row?.[key], column)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}

                        {/* Total Row */}
                        <tr className="border-t border-slate-200 bg-slate-50">
                          {columns.map((column, index) => {
                            const key = getColumnKey(column);
                            const totalValue = totalRow[key];

                            return (
                              <td
                                key={key || index}
                                className="whitespace-nowrap px-3 py-2.5 text-sm font-semibold text-slate-800"
                              >
                                {index === 0
                                  ? "Total"
                                  : formatCellValue(totalValue, column)}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex h-48 items-center justify-center bg-white">
                    <div className="text-center">
                      <p className="text-sm font-medium text-slate-700">
                        No data found
                      </p>

                      <p className="mt-1 text-xs text-slate-400">
                        Try changing the selected date range or project.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {data.length > 0 && (
                <div className="flex items-center justify-end gap-5 px-1 pt-3 text-[10px] text-slate-500">
                  <div className="flex items-center gap-2">
                    <span>Rows per page:</span>

                    <select
                      value={rowsPerPage}
                      onChange={(event) => {
                        setRowsPerPage(Number(event.target.value));
                        setCurrentPage(1);
                      }}
                      className="h-7 rounded-md border border-slate-200 bg-white px-2 text-[10px] font-medium text-slate-700 outline-none focus:border-[#283493]"
                    >
                      {PAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => page - 1)}
                      className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <FaChevronLeft className="text-[9px]" />
                    </button>

                    <span className="min-w-[65px] text-center font-medium text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      type="button"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((page) => page + 1)}
                      className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <FaChevronRight className="text-[9px]" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default ReusableTable;
